import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { blogPosts } from '../../mocks/blog-posts';
import PageMeta from '../../components/base/PageMeta';
import JsonLd from '../../components/base/JsonLd';
import FloatingCtaBar from '../../components/feature/FloatingCtaBar';
import SignUpModal from '../../components/feature/SignUpModal';
import SiteFooter from '../../components/feature/SiteFooter';

const CATEGORIES = [
  'All', 'Software', 'SaaS', 'AI', 'Guides', 'Comparisons',
  'Marketing', 'SEO', 'Hosting', 'Productivity', 'Design',
  'Social Media', 'CRM', 'Video', 'E-commerce', 'Project Management',
];

const TAG_COLORS: Record<string, string> = {
  'Software': 'text-teal-600 bg-teal-50',
  'SaaS': 'text-teal-600 bg-teal-50',
  'AI': 'text-teal-600 bg-teal-50',
  'Guides': 'text-gray-600 bg-gray-100',
  'Comparisons': 'text-violet-600 bg-violet-50',
  'Marketing': 'text-orange-600 bg-orange-50',
  'SEO': 'text-green-700 bg-green-50',
  'Hosting': 'text-sky-600 bg-sky-50',
  'Productivity': 'text-purple-600 bg-purple-50',
  'Design': 'text-orange-600 bg-orange-50',
  'Social Media': 'text-pink-600 bg-pink-50',
  'CRM': 'text-teal-600 bg-teal-50',
  'Video': 'text-red-600 bg-red-50',
  'E-commerce': 'text-teal-600 bg-teal-50',
  'Project Management': 'text-indigo-600 bg-indigo-50',
};

export default function BlogPage() {
  const { i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('All');
  const [visibleCount, setVisibleCount] = useState(16);
  const [showSignUp, setShowSignUp] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const currentLang = i18n.language === 'vi' ? 'VI' : 'EN';

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';

  const pageSchema = {
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: '247digitrend Blog – Digital Tool Insights & Guides',
    url: `${siteUrl}/blog`,
    description:
      'Expert insights, comprehensive guides, and the latest trends in online tools and software to help you make better decisions for your business.',
    publisher: {
      '@type': 'Organization',
      name: '247digitrend',
      url: siteUrl,
    },
    isPartOf: { '@id': `${siteUrl}/#website` },
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: siteUrl },
        { '@type': 'ListItem', position: 2, name: 'Blog', item: `${siteUrl}/blog` },
      ],
    },
  };

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filtered = activeCategory === 'All'
    ? blogPosts
    : blogPosts.filter(a => a.category === activeCategory || a.tags.includes(activeCategory));

  const visible = filtered.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>
      <PageMeta
        title="Blog – Digital Tool Guides, Reviews & Insights | 247digitrend"
        description="Read expert guides, AI tool reviews, SaaS comparisons and digital marketing insights on the 247digitrend blog. Tips to help your business grow smarter."
        canonical={`${siteUrl}/blog`}
        keywords="digital tools blog, AI tools guide, SaaS tips, software review articles, compare247 blog"
        ogType="blog"
      />
      <JsonLd data={pageSchema} />
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap" rel="stylesheet" />

      {/* ── SIGN UP MODAL ── */}
      <SignUpModal isOpen={showSignUp} onClose={() => setShowSignUp(false)} />

      {/* ── HEADER ── */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-sm shadow-sm' : 'bg-white'}`}>
        <div className="mx-auto px-8 py-2 flex items-center justify-between">
          <a href="/" className="flex items-center cursor-pointer">
            <img src="https://storage.readdy-site.link/project_files/3785ac0c-31ac-4f9e-b2ab-27ad3777db2a/7544398a-500f-41a6-81c3-aa7efbf1c0d2_logo-247.png?v=04135332188f9bc8b00780c66cd5837d" alt="247digitrend" className="h-36 w-auto object-contain" />
          </a>
          <nav className="hidden md:flex items-center gap-8">
            <a href="/" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Home</a>
            <a href="/categories" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Categories</a>
            <a href="/reviews" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Reviews</a>
            <a href="/comparisons" className="text-[19px] font-medium text-gray-700 hover:text-violet-600 transition-colors cursor-pointer">Comparisons</a>
            <a href="/blog" className="text-[19px] font-medium text-violet-600 font-semibold cursor-pointer">Blog</a>
          </nav>
          <div className="flex items-center gap-3">
            <div className="relative" ref={langRef}>
              <button onClick={() => setLangOpen(!langOpen)} className="flex items-center gap-1.5 bg-gray-100 hover:bg-gray-200 transition-colors rounded-full px-3 py-1.5 cursor-pointer">
                <i className="ri-translate-2 text-xs text-gray-500"></i>
                <span className="text-xs font-semibold text-gray-700">{currentLang}</span>
                <i className={`ri-arrow-down-s-line text-xs text-gray-400 transition-transform ${langOpen ? 'rotate-180' : ''}`}></i>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1.5 bg-white rounded-xl border border-gray-100 overflow-hidden w-32" style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
                  {(['VI', 'EN'] as const).map((lang) => (
                    <button key={lang} onClick={() => { i18n.changeLanguage(lang.toLowerCase()); setLangOpen(false); }} className={`w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold transition-colors cursor-pointer ${currentLang === lang ? 'bg-violet-50 text-violet-700' : 'text-gray-600 hover:bg-gray-50'}`}>
                      <span>{lang === 'VI' ? '🇻🇳' : '🇺🇸'}</span>
                      <span>{lang === 'VI' ? 'Tiếng Việt' : 'English'}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button onClick={() => setShowSignUp(true)} className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold px-5 py-2 rounded-full whitespace-nowrap cursor-pointer hover:opacity-90 transition-opacity">
              Sign Up
            </button>
          </div>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-44 pb-12" style={{ background: 'linear-gradient(160deg, #faf5ff 0%, #fdf2ff 40%, #fff0fb 100%)' }}>
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-5xl font-black text-gray-900 mb-3 tracking-tight">
            Our{' '}
            <span
              className="text-transparent bg-clip-text"
              style={{ backgroundImage: 'linear-gradient(90deg, #9333ea, #c026d3, #e879f9)' }}
            >
              Blog
            </span>
          </h1>
          <p className="text-base text-gray-500 leading-relaxed max-w-xl mx-auto">
            Expert insights, comprehensive guides, and the latest trends in online tools and software to help you make better decisions for your business.
          </p>
        </div>
      </section>

      {/* ── CATEGORY TABS ── */}
      <div className="sticky top-[60px] z-40 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-6xl mx-auto px-8 py-3 flex flex-wrap gap-2">
          {CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => { setActiveCategory(cat); setVisibleCount(16); }}
              className={`text-xs font-semibold px-4 py-1.5 rounded-full whitespace-nowrap cursor-pointer transition-all ${
                activeCategory === cat
                  ? 'text-white'
                  : 'text-gray-600 bg-gray-100 hover:bg-gray-200'
              }`}
              style={activeCategory === cat ? { background: 'linear-gradient(90deg, #0d9488, #06b6d4)' } : {}}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* ── ARTICLES GRID ── */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-8">
          {visible.length > 0 ? (
            <div className="grid grid-cols-3 gap-6 mb-10">
              {visible.map((article, i) => (
                <div
                  key={i}
                  onClick={() => navigate(`/blog/${article.slug}`)}
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:border-violet-200 hover:shadow-lg transition-all cursor-pointer block"
                >
                  {/* Image */}
                  <div className="w-full h-44 overflow-hidden">
                    <img
                      src={article.img}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  {/* Body */}
                  <div className="p-4">
                    {/* Tags + read time */}
                    <div className="flex flex-wrap items-center gap-1.5 mb-2.5">
                      {article.tags.map((tag, ti) => (
                        <span
                          key={ti}
                          className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${TAG_COLORS[tag] ?? 'text-gray-600 bg-gray-100'}`}
                        >
                          {tag}
                        </span>
                      ))}
                      <span className="text-xs text-gray-400 ml-auto whitespace-nowrap">{article.read}</span>
                    </div>
                    {/* Author */}
                    <div className="flex items-center gap-2 mb-2.5">
                      <div className="w-7 h-7 rounded-full overflow-hidden flex-shrink-0 border border-violet-100">
                        <img src={article.avatarUrl} alt={article.author} className="w-full h-full object-cover object-top" />
                      </div>
                      <span className="text-xs font-medium text-gray-700">{article.author}</span>
                      <span className="text-xs text-gray-400">{article.date}</span>
                    </div>
                    {/* Title */}
                    <h2 className="text-sm font-bold text-gray-900 mb-2 leading-snug line-clamp-2 group-hover:text-violet-700 transition-colors">
                      {article.title}
                    </h2>
                    {/* Desc */}
                    <p className="text-xs text-gray-500 leading-relaxed mb-3 line-clamp-2">{article.desc}</p>
                    {/* Read More */}
                    <span className="text-xs font-semibold text-teal-600 inline-flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                      Read More <i className="ri-arrow-right-line"></i>
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-20 text-gray-400 text-sm">No articles found in this category.</div>
          )}

          {/* Load More */}
          {visibleCount < filtered.length && (
            <div className="text-center">
              <button
                onClick={() => setVisibleCount(v => v + 9)}
                className="inline-flex items-center gap-2 text-white text-sm font-semibold px-8 py-3 rounded-full cursor-pointer hover:opacity-90 transition-opacity whitespace-nowrap"
                style={{ background: 'linear-gradient(90deg, #0d9488, #06b6d4)' }}
              >
                Load More Articles <i className="ri-arrow-down-line"></i>
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── FOOTER ── */}
      <SiteFooter />

      <FloatingCtaBar onSignUp={() => setShowSignUp(true)} />
    </div>
  );
}
